import { useId, useState, type FormEvent } from 'react';
import type {
  Contrato,
  EvaluacionNioCompleta,
  EvaluacionNioReducida,
  Party,
} from '@matias-asset-os/contracts';
import { formatearImporteDesdeCents, t, type Idioma } from '@matias-asset-os/i18n';
import { api, ErrorApi } from '../api.js';
import { fechaLarga } from '../componentes.js';
import { AccionConfirmada } from './AccionConfirmada.js';

interface Props {
  propiedadId: string;
  contratos: Contrato[];
  partes: Party[];
  evaluaciones: Array<EvaluacionNioCompleta | EvaluacionNioReducida>;
  idioma: Idioma;
  onCambio: () => void;
}

/**
 * Contrato de gestion y su tarifa. Exclusivo de Direccion.
 *
 * Tres momentos, en este orden y no en otro: se crea en borrador con su cliente
 * de facturacion, se le fija la tarifa —manual o por nivel operativo— y se
 * activa. Al activarlo el importe queda CONGELADO: cambiarlo despues exige una
 * tarifa nueva con su propia vigencia, no reescribir la anterior.
 */
export function PanelContrato({
  propiedadId,
  contratos,
  partes,
  evaluaciones,
  idioma,
  onCambio,
}: Props) {
  const ids = {
    party: useId(),
    desde: useId(),
    moneda: useId(),
    umbral: useId(),
    origen: useId(),
    importe: useId(),
    evaluacion: useId(),
  };

  const [billingPartyId, setBillingPartyId] = useState('');
  const [desde, setDesde] = useState('');
  const [moneda, setMoneda] = useState('EUR');
  const [umbral, setUmbral] = useState('500');
  const [origen, setOrigen] = useState<'MANUAL' | 'NIO'>('NIO');
  const [importe, setImporte] = useState('');
  const [error, setError] = useState<string | null>(null);

  const vigente = contratos.find((c) => c.estado === 'ACTIVE');
  const borrador = contratos.find((c) => c.estado === 'DRAFT');
  const historicos = contratos.filter((c) => c.estado === 'TERMINATED');
  const validada = evaluaciones.find((e) => e.estado === 'VALIDADA');

  const conError = async (accion: () => Promise<unknown>) => {
    setError(null);
    try {
      await accion();
      onCambio();
    } catch (fallo: unknown) {
      setError(
        fallo instanceof ErrorApi ? t(idioma, fallo.mensajeClave) : t(idioma, 'error.servidor'),
      );
    }
  };

  const crear = (evento: FormEvent) => {
    evento.preventDefault();
    void conError(() =>
      api.crearContrato(propiedadId, {
        billingPartyId,
        ...(desde ? { desde } : {}),
        moneda,
        // El umbral se introduce en euros y se guarda en centimos enteros.
        umbralAprobacionCents: Math.round(Number(umbral) * 100),
      }),
    );
  };

  const fijarTarifa = (evento: FormEvent) => {
    evento.preventDefault();
    if (!borrador) return;
    void conError(() =>
      api.fijarTarifa(
        borrador.id,
        origen === 'NIO'
          ? {
              origen: 'NIO',
              nioEvaluationId: validada!.id,
              ...(importe ? { importeCents: Math.round(Number(importe) * 100) } : {}),
            }
          : { origen: 'MANUAL', importeCents: Math.round(Number(importe) * 100) },
      ),
    );
  };

  const importeDe = (contrato: Contrato) =>
    contrato.tarifaVigente
      ? formatearImporteDesdeCents(contrato.tarifaVigente.cents, idioma, {
          moneda: contrato.tarifaVigente.moneda,
        })
      : '—';

  return (
    <section className="tarjeta">
      <h2>{t(idioma, 'contrato.titulo')}</h2>

      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}

      {vigente && (
        <ul>
          <li>
            <strong>{vigente.codigo}</strong> · {t(idioma, `contrato.${vigente.estado}`)}
          </li>
          <li>
            {t(idioma, 'contrato.clienteFacturacion')}: {vigente.clienteFacturacion?.nombre ?? '—'}
          </li>
          <li>
            {t(idioma, 'contrato.tarifa')}: {importeDe(vigente)} (
            {t(idioma, `contrato.modalidad.${vigente.modalidadTarifa}`)}) ·{' '}
            <small>{t(idioma, 'contrato.congelada')}</small>
          </li>
          <li>
            {t(idioma, 'contrato.umbral')}:{' '}
            {formatearImporteDesdeCents(vigente.umbralAprobacion.cents, idioma, {
              moneda: vigente.umbralAprobacion.moneda,
            })}
          </li>
        </ul>
      )}

      {vigente && (
        <div className="acciones">
          <AccionConfirmada
            etiqueta={t(idioma, 'contrato.finalizar')}
            consecuencias={t(idioma, 'contrato.finalizarConsecuencias')}
            idioma={idioma}
            pideMotivo
            etiquetaFecha={t(idioma, 'contrato.hasta')}
            onConfirmar={({ motivo, fecha }) =>
              void conError(() => api.finalizarContrato(vigente.id, { motivo, hasta: fecha }))
            }
          />
        </div>
      )}

      {borrador && (
        <>
          <ul>
            <li>
              <strong>{borrador.codigo}</strong> · {t(idioma, `contrato.${borrador.estado}`)}
            </li>
            <li>
              {t(idioma, 'contrato.clienteFacturacion')}:{' '}
              {borrador.clienteFacturacion?.nombre ?? '—'}
            </li>
            <li>
              {t(idioma, 'contrato.tarifa')}: {importeDe(borrador)}
            </li>
          </ul>

          <form className="formulario" onSubmit={fijarTarifa}>
            <div className="campo">
              <label htmlFor={ids.origen}>{t(idioma, 'contrato.modalidadTarifa')}</label>
              <select
                id={ids.origen}
                value={origen}
                onChange={(e) => setOrigen(e.target.value as 'MANUAL' | 'NIO')}
              >
                <option value="NIO">{t(idioma, 'contrato.modalidad.NIO')}</option>
                <option value="MANUAL">{t(idioma, 'contrato.modalidad.MANUAL')}</option>
              </select>
            </div>

            {origen === 'NIO' && !validada && (
              <p className="error">{t(idioma, 'contrato.nioNoValidada')}</p>
            )}

            {origen === 'NIO' && validada && (
              <p id={ids.evaluacion}>
                {t(idioma, 'nio.titulo')}: {t(idioma, 'propiedades.nio')} {validada.nivel}
              </p>
            )}

            <div className="campo">
              <label htmlFor={ids.importe}>
                {t(idioma, 'contrato.importe')}
                {origen === 'NIO' ? ` (${t(idioma, 'contrato.importeOpcional')})` : ''}
              </label>
              <input
                id={ids.importe}
                type="number"
                min={0}
                step="0.01"
                required={origen === 'MANUAL'}
                value={importe}
                onChange={(e) => setImporte(e.target.value)}
              />
            </div>

            <div className="acciones">
              <button type="submit" disabled={origen === 'NIO' && !validada}>
                {t(idioma, 'contrato.fijarTarifa')}
              </button>
              <button
                type="button"
                disabled={borrador.tarifaVigente === null}
                onClick={() => void conError(() => api.activarContrato(borrador.id))}
              >
                {t(idioma, 'contrato.activar')}
              </button>
            </div>
          </form>
        </>
      )}

      {!vigente && !borrador && (
        <form className="formulario" onSubmit={crear}>
          <p>{t(idioma, 'contrato.vacio')}</p>
          <div className="campo">
            <label htmlFor={ids.party}>{t(idioma, 'contrato.clienteFacturacion')}</label>
            <select
              id={ids.party}
              required
              value={billingPartyId}
              onChange={(e) => setBillingPartyId(e.target.value)}
            >
              <option value="">—</option>
              {partes.map((party) => (
                <option key={party.id} value={party.id}>
                  {party.nombre}
                </option>
              ))}
            </select>
          </div>
          <div className="campo">
            <label htmlFor={ids.desde}>{t(idioma, 'contrato.desde')}</label>
            <input
              id={ids.desde}
              type="date"
              value={desde}
              onChange={(e) => setDesde(e.target.value)}
            />
          </div>
          <div className="campo">
            <label htmlFor={ids.moneda}>{t(idioma, 'contrato.moneda')}</label>
            <input
              id={ids.moneda}
              required
              maxLength={3}
              value={moneda}
              onChange={(e) => setMoneda(e.target.value.toUpperCase())}
            />
          </div>
          <div className="campo">
            <label htmlFor={ids.umbral}>{t(idioma, 'contrato.umbral')}</label>
            <input
              id={ids.umbral}
              type="number"
              min={0}
              step="0.01"
              required
              value={umbral}
              onChange={(e) => setUmbral(e.target.value)}
            />
          </div>
          <button type="submit">{t(idioma, 'contrato.crear')}</button>
        </form>
      )}

      {historicos.length > 0 && (
        <details>
          <summary>{t(idioma, 'contrato.historicos')}</summary>
          <ul>
            {historicos.map((contrato) => (
              <li key={contrato.id}>
                {contrato.codigo} · {fechaLarga(contrato.desde, idioma)} →{' '}
                {fechaLarga(contrato.hasta, idioma)} · {importeDe(contrato)}
              </li>
            ))}
          </ul>
        </details>
      )}
    </section>
  );
}
